// src/components/results/ResultsLayout.js
//
// Ticket 069 (decisions D9 to D11). Presentational and product-agnostic: no intake
// imports. A tab strip, the active panel, a sticky right rail on wide screens and a
// compact sticky bottom bar on narrow ones. Adopted by the Advisor Intake Results
// page only; the Snapshot and Calculator result pages may adopt it later.
//
// Props:
//   tabs      [{ id, label }]
//   active    the active tab id (the caller owns the state)
//   onTab     (id) => void
//   above     optional node shown above the tabs (header, boxes)
//   rail      node for the desktop rail (hidden below 900px by CSS)
//   mobileBar node for the compact bottom bar (hidden from 900px by CSS)
//   children  the active panel's contents
//
// One stable tree (rule 12): only the panel contents change. Sticky, not fixed
// (hygiene H3): everything stays in the page flow. Tabs follow the WAI-ARIA pattern:
// role tablist / tab / tabpanel, aria-selected, arrow keys, Home and End move between
// tabs (focus follows, automatic activation).

import React, { useRef } from 'react'

export default function ResultsLayout({ tabs, active, onTab, above = null, rail = null, mobileBar = null, children }) {
  const refs = useRef({})

  function onKeyDown(e, index) {
    let next = null
    if (e.key === 'ArrowRight') next = (index + 1) % tabs.length
    else if (e.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = tabs.length - 1
    if (next === null) return
    e.preventDefault()
    const id = tabs[next].id
    onTab(id)
    const el = refs.current[id]
    if (el) el.focus()
  }

  return (
    <div className="results-layout">
      <div className="results-layout-grid">
        <div className="results-layout-main">
          {above}
          <div className="results-layout-tabs" role="tablist" aria-label="Results">
            {tabs.map((t, i) => (
              <button
                key={t.id}
                ref={(el) => { refs.current[t.id] = el }}
                type="button"
                role="tab"
                id={`results-tab-${t.id}`}
                aria-selected={t.id === active}
                aria-controls={`results-panel-${t.id}`}
                tabIndex={t.id === active ? 0 : -1}
                className={`results-layout-tab${t.id === active ? ' is-active' : ''}`}
                onClick={() => onTab(t.id)}
                onKeyDown={(e) => onKeyDown(e, i)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div
            className="results-layout-panel"
            role="tabpanel"
            id={`results-panel-${active}`}
            aria-labelledby={`results-tab-${active}`}
          >
            {children}
          </div>
        </div>
        {rail && <aside className="results-layout-rail">{rail}</aside>}
      </div>
      {mobileBar && <div className="results-layout-bar">{mobileBar}</div>}
    </div>
  )
}
